/**
 * Wizard step: test level selection — bound to the project SoilTestLevel
 * standard, with Phase 11 plan locks (UX only; API enforces).
 */
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Grid,
	Radio,
	Stack,
	Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { SoilTestLevel } from "@flaha/shared-types";
import { Link as RouterLink } from "react-router-dom";

import { TEST_LEVEL_OPTIONS } from "../utils/soilTestDefaults";

export interface TestLevelPlanAccess {
	allowPreliminary: boolean;
	allowModerate: boolean;
	allowAdvanced: boolean;
	tierLabel?: string;
}

interface TestLevelStepProps {
	value: SoilTestLevel;
	onChange: (next: SoilTestLevel) => void;
	/** When omitted, all levels are selectable (e.g. mock without usage). */
	planAccess?: TestLevelPlanAccess | null;
}

function isLevelAllowed(
	level: SoilTestLevel,
	access: TestLevelPlanAccess | null | undefined
): boolean {
	if (!access) return true;
	if (level === SoilTestLevel.PRELIMINARY) return access.allowPreliminary;
	if (level === SoilTestLevel.MODERATE) return access.allowModerate;
	if (level === SoilTestLevel.ADVANCED) return access.allowAdvanced;
	return false;
}

function lockMessage(level: SoilTestLevel): {
	title: string;
	body: string;
	ctaTo: string;
	ctaLabel: string;
} {
	if (level === SoilTestLevel.ADVANCED) {
		return {
			title: "Advanced testing is available under the Enterprise plan.",
			body: "Contact Flaha for contract limits, branding, and Advanced laboratory fields.",
			ctaTo: "/enterprise/contact",
			ctaLabel: "Contact Enterprise",
		};
	}
	return {
		title: "Moderate testing is available on the Professional plan.",
		body: "Upgrade to unlock Moderate chemistry panels, projects, and standard PDF reports.",
		ctaTo: "/account?tab=billing",
		ctaLabel: "View billing",
	};
}

export function TestLevelStep({
	value,
	onChange,
	planAccess,
}: TestLevelStepProps) {
	return (
		<Box>
			<Typography variant="h6" gutterBottom>
				Test level (evidence standard)
			</Typography>
			<Typography color="text.secondary" sx={{ mb: 2 }}>
				Choose the analysis scope that matches the laboratory fields you will
				submit. The declared level is the project standard for API coverage
				scoring, wizard panels, and report evidence honesty — it does not
				silently downgrade if you omit fields.
			</Typography>

			<Alert severity="info" sx={{ mb: 3 }} variant="outlined">
				<Typography variant="body2" component="div">
					<strong>Hierarchy:</strong> Moderate includes Preliminary; Advanced
					includes Moderate. Extra fields beyond the selected level are allowed
					as supplementary evidence.
					{planAccess?.tierLabel
						? ` Your plan: ${planAccess.tierLabel}.`
						: ""}
				</Typography>
			</Alert>

			<Box component="fieldset" sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
				<Box
					component="legend"
					sx={{
						position: "absolute",
						width: 1,
						height: 1,
						overflow: "hidden",
						clip: "rect(0 0 0 0)",
					}}
				>
					Select an analysis level
				</Box>
				<Grid container spacing={2} role="radiogroup" aria-label="Test level">
					{TEST_LEVEL_OPTIONS.map((opt) => {
						const selected = value === opt.value;
						const allowed = isLevelAllowed(opt.value, planAccess);
						const lock = !allowed ? lockMessage(opt.value) : null;
						return (
							<Grid item xs={12} md={4} key={opt.value}>
								<Card
									component={allowed ? "label" : "div"}
									variant="outlined"
									sx={{
										display: "block",
										height: "100%",
										cursor: allowed ? "pointer" : "not-allowed",
										opacity: allowed ? 1 : 0.88,
										borderColor: selected
											? "primary.main"
											: allowed
												? "divider"
												: "warning.main",
										borderWidth: selected || !allowed ? 2 : 1,
										transition: (theme) => theme.motion.transition.surface,
										"&:focus-within": allowed
											? {
													outline: "3px solid",
													outlineColor: "workspace.blue",
													outlineOffset: 2,
												}
											: undefined,
									}}
								>
									<CardContent>
										<Stack
											direction="row"
											spacing={1}
											alignItems="center"
											sx={{ mb: 1 }}
											flexWrap="wrap"
											useFlexGap
										>
											{allowed ? (
												<Radio
													checked={selected}
													value={opt.value}
													name="test-level"
													size="small"
													onChange={() => onChange(opt.value)}
													inputProps={{
														"aria-label": `Select ${opt.label}`,
													}}
													sx={{ p: 0.5 }}
												/>
											) : (
												<LockOutlinedIcon
													fontSize="small"
													sx={{ color: "warning.main" }}
													aria-hidden
												/>
											)}
											<Typography variant="subtitle1" fontWeight={700}>
												{opt.label}
											</Typography>
											<Chip
												label={opt.value}
												size="small"
												variant="outlined"
												sx={{ fontFamily: "monospace", fontSize: "0.65rem" }}
											/>
											{selected && allowed ? (
												<Chip
													label="Selected"
													size="small"
													color="primary"
													variant="outlined"
												/>
											) : null}
											{!allowed ? (
												<Chip
													label="Upgrade"
													size="small"
													color="warning"
													variant="outlined"
												/>
											) : null}
										</Stack>
										{lock && (
											<Alert severity="warning" sx={{ mb: 1.5 }} variant="outlined">
												<Typography variant="body2" fontWeight={600}>
													{lock.title}
												</Typography>
												<Typography variant="body2" sx={{ mb: 1 }}>
													{lock.body}
												</Typography>
												<Button
													component={RouterLink}
													to={lock.ctaTo}
													size="small"
													variant="outlined"
												>
													{lock.ctaLabel}
												</Button>
											</Alert>
										)}
										<Typography variant="body2" sx={{ mb: 1.5 }}>
											{opt.description}
										</Typography>
										<MetaRow label="Expected fields" value={opt.captures} />
										<MetaRow label="Best for" value={opt.bestFor} />
										{opt.addedFieldLabels.length > 0 ? (
											<Box sx={{ mt: 1.25 }}>
												<Typography
													variant="caption"
													color="text.secondary"
													sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
												>
													Added at this level
												</Typography>
												<Stack
													direction="row"
													flexWrap="wrap"
													useFlexGap
													gap={0.5}
													sx={{ mt: 0.5 }}
												>
													{opt.addedFieldLabels.map((label) => (
														<Chip
															key={label}
															label={label}
															size="small"
															variant="outlined"
															sx={{ height: 22, fontSize: "0.7rem" }}
														/>
													))}
												</Stack>
											</Box>
										) : null}
									</CardContent>
								</Card>
							</Grid>
						);
					})}
				</Grid>
			</Box>
		</Box>
	);
}

function MetaRow({ label, value }: { label: string; value: string }) {
	return (
		<Box sx={{ mb: 1 }}>
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
			>
				{label}
			</Typography>
			<Typography variant="body2" sx={{ lineHeight: 1.45 }}>
				{value}
			</Typography>
		</Box>
	);
}
